import { Loader } from '@oriola-corporation/kronan-icons';
import React from 'react';
import { cn } from '../../utils';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  variant?: 'primary' | 'secondary';
  isInverted?: boolean;
  isLoading?: boolean;
  'aria-label': string;
}

const IconButtonComponent = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = 'primary',
      isInverted = false,
      isLoading = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    // Prevent clicks when button is loading
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        className={cn(
          `ripple flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full p-1 text-2xl`,
          variant === 'primary' &&
            (isInverted
              ? 'bg-white text-primary hover:bg-green-50'
              : 'bg-primary text-white hover:bg-primaryDarker'),
          variant === 'secondary' &&
            (isInverted
              ? 'bg-transparent text-white hover:bg-white/10'
              : 'bg-transparent text-text hover:bg-green-50'),
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {isLoading ? <Loader isRotating /> : icon}
      </button>
    );
  }
);

IconButtonComponent.displayName = 'IconButton';

/**
 * A button component that displays only an icon, without text.
 *
 * @param {IconButtonProps} props - IconButton component props
 * @param {React.ReactNode} props.icon - Icon to display (required).
 * @param {boolean} [props.variant] - The visual style variant ('primary' or 'secondary')
 * @param {boolean} [props.isInverted] - Invert colors for use on dark backgrounds
 * @param {boolean} [props.isLoading] - Show a loading spinner instead of the icon
 * @param {string} props['aria-label'] - Accessible label for the button (required)
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref to the underlying button element
 */
export const IconButton = IconButtonComponent;
